import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ExternalLink, ShieldCheck } from 'lucide-react';
import { pastDraws } from '@/lib/api';
import { formatDateLong } from '@/lib/format';

// Show the seed in a compact form; full seed appears on the verify page.
const shortSeed = (s) => {
  if (s === null || s === undefined) return '—';
  const str = String(s);
  if (str.length <= 16) return str;
  return `${str.slice(0, 8)}…${str.slice(-4)}`;
};

const PastDrawsPage = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    pastDraws().then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);

  return (
    <div className="container-pd py-14 md:py-20 max-w-6xl" data-testid="past-draws-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Draw audit trail</div>
      <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Past draws.</h1>
      <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--pd-gold)]" data-testid="past-draws-motto">Equal Odds, Equal Opportunity.</p>
      <p className="mt-5 text-lg text-muted-sage leading-relaxed max-w-3xl">
        Every draw we have ever run, with the seed, algorithm, eligible ticket list, winning ticket, and independent witness recorded. Click <span className="text-[color:var(--pd-text)]">Verify</span> on any row to recompute the pick yourself. For the human side of the same information, see our{' '}
        <Link to="/winners" className="text-[color:var(--pd-accent-yellow)] underline">winners archive</Link>. For the method, see{' '}
        <Link to="/how-we-draw-winners" className="text-[color:var(--pd-accent-yellow)] underline">How we draw winners</Link>.
      </p>

      {items === null ? (
        <div className="mt-10 text-sm text-muted-sage">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-pd bg-surface p-8 max-w-2xl" data-testid="past-draws-empty">
          <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Launch mode</div>
          <h2 className="font-display text-2xl mt-2">No draws yet.</h2>
          <p className="mt-3 text-sm text-muted-sage">
            Once our first live competition closes and is drawn, the full details will appear here — seed, eligible ticket list, witness, and winning ticket — dated and permanent. Nothing gets edited or removed from this log after publication.
          </p>
        </div>
      ) : (
        <div className="mt-10 rounded-2xl border border-pd bg-surface overflow-hidden" data-testid="past-draws-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--pd-bg)]">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-sage">
                  <th className="px-4 py-3">Competition</th>
                  <th className="px-4 py-3">Drawn</th>
                  <th className="px-4 py-3 text-right">Valid entries</th>
                  <th className="px-4 py-3">Seed</th>
                  <th className="px-4 py-3">Winning ticket</th>
                  <th className="px-4 py-3">Witness</th>
                  <th className="px-4 py-3">Verify</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d, i) => (
                  <tr key={d.id} className={i % 2 === 0 ? 'bg-transparent' : 'bg-[color:var(--pd-bg)]/40'} data-testid="past-draw-row">
                    <td className="px-4 py-3">
                      {d.competition_slug ? (
                        <Link to={`/competitions/${d.competition_slug}`} className="text-[color:var(--pd-text)] hover:text-[color:var(--pd-accent-yellow)]">{d.competition_title || d.competition_slug}</Link>
                      ) : (
                        <span className="text-[color:var(--pd-text)]">{d.competition_title || '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-sage">{formatDateLong(d.drawn_at)}</td>
                    <td className="px-4 py-3 text-right font-mono">{(d.eligible_count || 0).toLocaleString('en-GB')}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-sage" title={String(d.seed ?? '—')}>{shortSeed(d.seed)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[color:var(--pd-text)]">{d.winner_ticket_number ?? '—'}</td>
                    <td className="px-4 py-3">
                      {d.witnessed ? (
                        <span
                          className="inline-flex items-center gap-1.5 text-[color:var(--pd-text)]"
                          title={d.witness_name ? undefined : 'An independent observer confirmed this draw live. Their full name is kept on the internal audit trail even though it is not shown publicly here.'}
                        >
                          <CheckCircle2 size={14} className="text-[color:var(--pd-gold)]" />
                          {d.witness_name ? (
                            <span>{d.witness_name}{d.witness_role ? <span className="text-muted-sage">, {d.witness_role}</span> : null}</span>
                          ) : (
                            <span>Independently witnessed</span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[color:var(--pd-danger)]">
                          <AlertTriangle size={14} />
                          <span>No witness recorded</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/past-draws/${d.id}/verify`}
                        className="inline-flex items-center gap-1 text-xs border border-pd rounded-lg px-2 py-1 hover:border-[color:var(--pd-accent-yellow)] hover:text-[color:var(--pd-accent-yellow)]"
                        data-testid={`verify-draw-${d.id}`}
                      >
                        Verify <ExternalLink size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <section
        className="mt-8 rounded-2xl border border-pd bg-surface p-5 md:p-6 max-w-3xl"
        data-testid="witness-legend"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck
            size={18}
            className="shrink-0 mt-0.5 text-[color:var(--pd-gold)]"
            aria-hidden="true"
          />
          <div>
            <h2 className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">
              About the witness record
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-sage leading-relaxed">
              <li>
                <span className="text-[color:var(--pd-text)]">A name shown in the Witness column</span>{' '}
                — the person named observed the draw live and consented to be identified publicly.
              </li>
              <li>
                <span className="text-[color:var(--pd-text)]">&ldquo;Independently witnessed&rdquo;</span>{' '}
                — an independent observer confirmed the draw live and the record was signed by a
                time-stamped admin session. Their full name and role are retained on the internal
                audit trail even when not published here, and can be produced on request to a
                regulator or a formal complainant.
              </li>
              <li>
                <span className="text-[color:var(--pd-text)]">&ldquo;No witness recorded&rdquo;</span>{' '}
                — no independent observer was present at that draw. The mathematical audit trail
                (seed, algorithm, eligible ticket list) still lets anyone re-run the pick for
                themselves.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <p className="mt-6 text-xs text-muted-sage">
        Every draw is decided by a cryptographically-seeded random number generator with a fully published audit trail, and additionally confirmed live by an independent witness. See <Link to="/how-we-draw-winners" className="text-[color:var(--pd-accent-yellow)]">How we draw winners</Link> for the operational detail.
      </p>
    </div>
  );
};

export default PastDrawsPage;
